"use client"

import * as React from "react"
import { 
  Menu, X, ArrowUpRight, Activity, 
  Send, Terminal, LayoutDashboard, 
  Monitor, Shield, Zap, Globe, Lock, Brain,
  ExternalLink, Layers, RefreshCw, CheckCircle2,
  Wallet, PieChart
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { motion, AnimatePresence } from "framer-motion"
import { io, Socket } from "socket.io-client"
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts'

// --- TYPES ---
interface Trade {
  side: string
  size: number
  price: number
  market: string
  outcome: string
}

interface Order {
  side: string
  size: number
  price: number
  market: string
  token_id?: string
}

interface Position {
  size: number
  outcome: string
  market: string
  curPrice?: number
  percentPnl?: number
  asset?: string
  conditionId?: string
}

interface HistoryPoint {
  time: string
  value: number
}

interface DashboardData {
  balance: number
  balance_history: HistoryPoint[]
  last_scan: string
  status: string
  open_orders: Order[]
  recent_trades: Trade[]
  positions: Position[]
  logs: string[]
  settings: {
    max_trade_amount: number
    min_probability: number
    take_profit: number
    stop_loss: number
  }
}

// --- GLOBALS ---
let socket: Socket

// --- COMPONENTS ---

function PerformanceChart({ data }: { data: HistoryPoint[] }) {
  if (!data || data.length === 0) return (
    <div className="h-[260px] w-full bg-slate-50 rounded-[2rem] flex flex-col items-center justify-center border border-slate-100">
        <Activity className="h-8 w-8 text-slate-200 mb-2 animate-pulse" />
        <p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Syncing performance grid...</p>
    </div>
  )
  
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563EB" stopOpacity={0.1}/>
              <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="time" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} dy={10} />
          <YAxis hide domain={['auto', 'auto']} />
          <Tooltip 
            contentStyle={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px', fontSize: '10px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
            itemStyle={{ color: '#2563EB' }}
          />
          <Area 
            type="monotone" 
            dataKey="value" 
            stroke="#2563EB" 
            strokeWidth={3} 
            fillOpacity={1} 
            fill="url(#colorValue)" 
            animationDuration={1500}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

function StatCard({ title, value, subtext, icon: Icon, trend }: { title: string, value: string, subtext?: string, icon: React.ComponentType<React.SVGProps<SVGSVGElement>>, trend?: 'up' | 'down' | 'neutral' }) {
  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 rounded-[2rem] overflow-hidden group">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-2">
          {title}
        </CardTitle>
        <div className="h-8 w-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold tracking-tight text-slate-900 tabular-nums">{value}</div>
        {subtext && (
          <div className="flex items-center gap-2 mt-1">
            <p className="text-[11px] font-medium text-slate-400 uppercase">{subtext}</p>
            {trend === 'up' && <ArrowUpRight className="h-3 w-3 text-emerald-500" />}
            {trend === 'down' && <ArrowUpRight className="h-3 w-3 text-rose-500 rotate-90" />}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function StatusBadge({ status }: { status: string }) {
  const isRunning = status.toLowerCase().includes("trading") || status.toLowerCase().includes("running")
  return (
    <div className={cn("inline-flex items-center rounded-xl px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-all duration-300", 
      isRunning 
        ? "bg-emerald-50 text-emerald-600 border border-emerald-100" 
        : "bg-amber-50 text-amber-600 border border-amber-100"
    )}>
      <span className={cn("mr-2 h-2 w-2 rounded-full", isRunning ? "bg-emerald-500 animate-pulse" : "bg-amber-500")}></span>
      {status}
    </div>
  )
}

function ChatWidget() {
  const [isOpen, setIsOpen] = React.useState(false)
  const [messages, setMessages] = React.useState<{role: 'user' | 'bot', text: string}[]>([
    { role: 'bot', text: "Systems online. How can I assist with your trading portfolio today?" }
  ])
  const [input, setInput] = React.useState("")
  const scrollRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!socket) {
        const url = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? "http://localhost:5001" : undefined;
        socket = io(url, { autoConnect: true })
    }
    socket.on('bot_reply', (data: {reply: string}) => {
        setMessages(prev => [...prev, { role: 'bot', text: data.reply }])
    })
    return () => { socket.off('bot_reply') }
  }, [])

  React.useEffect(() => {
    const saved = localStorage.getItem('chatHistoryJSON')
    if (saved) {
      try { 
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed) && parsed.length > 0) setMessages(parsed) 
      } catch (e) { console.error(e) }
    }
  }, [])

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    if (messages.length > 1) localStorage.setItem('chatHistoryJSON', JSON.stringify(messages))
  }, [messages])

  function sendMessage() {
    if (!input.trim()) return
    setMessages(prev => [...prev, { role: 'user', text: input }])
    if (socket && socket.connected) {
        socket.emit('chat_message', { message: input })
    } else {
        socket.connect()
        setTimeout(() => socket.emit('chat_message', { message: input }), 1000)
    }
    setInput("")
  }

  return (
    <div className="fixed bottom-10 right-10 z-50 flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }} 
            animate={{ opacity: 1, y: 0, scale: 1 }} 
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="mb-6 w-[400px] overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-100 p-6 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl bg-primary flex items-center justify-center text-white">
                  <Brain className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Intelligence Assistant</h3>
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest">PolyClaw AI Engine</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl hover:bg-slate-100" onClick={() => setIsOpen(false)}>
                <X className="h-4 w-4 text-slate-400" />
              </Button>
            </div>
            <div className="h-[450px] overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-thumb-slate-200" ref={scrollRef}>
              {messages.map((m, i) => (
                <div key={i} className={cn("flex w-full animate-in fade-in slide-in-from-bottom-2", m.role === 'user' ? "justify-end" : "justify-start")}>
                  <div className={cn("max-w-[85%] rounded-[1.5rem] px-5 py-3 text-[13px] font-medium leading-relaxed shadow-sm", 
                    m.role === 'user' 
                      ? "bg-primary text-white rounded-tr-none" 
                      : "bg-slate-100/80 border border-slate-100 rounded-tl-none text-slate-700"
                  )}>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-slate-100 bg-slate-50/30 flex gap-3">
              <Input 
                value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()}
                placeholder="Ask about market analysis..." 
                className="flex-1 rounded-2xl h-12 bg-white border-slate-200 focus-visible:ring-primary text-sm shadow-sm"
              />
              <Button size="icon" className="h-12 w-12 rounded-2xl bg-primary text-white shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all" onClick={sendMessage}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <Button 
        size="lg" className={cn("h-16 w-16 rounded-2xl shadow-xl shadow-primary/30 transition-all hover:scale-110 active:scale-95 bg-primary text-white", isOpen && "scale-0 translate-y-20")}
        onClick={() => setIsOpen(true)}
      >
        <Brain className="h-8 w-8" />
      </Button>
    </div>
  )
}

export default function Dashboard() {
  const [data, setData] = React.useState<DashboardData | null>(null)
  const [sidebarOpen, setSidebarOpen] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState("OVERVIEW")

  React.useEffect(() => {
    if (!socket) {
        const url = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? "http://localhost:5001" : undefined;
        socket = io(url, { autoConnect: true })
    }
    socket.on('stats_update', (newData: DashboardData) => { setData(newData) })
    socket.on('new_log', (payload: {log: string}) => {
        setData(prev => {
            if (!prev) return prev
            return { ...prev, logs: [payload.log, ...prev.logs].slice(0, 50) }
        })
    })
    socket.on('settings_update', (newSettings) => {
        setData(prev => prev ? { ...prev, settings: newSettings } : prev)
    })
    fetch('/api/stats').then(res => res.json()).then(setData).catch(console.error)
    return () => { 
        socket.off('stats_update')
        socket.off('new_log')
        socket.off('settings_update')
    }
  }, [])

  const [saving, setSaving] = React.useState(false)
  const saveSettings = async (field: string, value: string | number) => {
    setSaving(true)
    try {
        const res = await fetch('/api/settings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ [field]: value })
        })
        const result = await res.json()
        if (result.success) {
            // Updated
        }
    } catch (e) {
        console.error("Save error", e)
    } finally {
        setSaving(false)
    }
  }

  const renderContent = () => {
    switch (activeTab) {
      case "ACTIVITY":
        return (
          <Card className="border-slate-200 bg-white shadow-sm rounded-[2.5rem] overflow-hidden">
            <CardHeader className="p-8 border-b border-slate-100 bg-slate-50/30">
              <CardTitle className="text-xl font-bold flex items-center gap-3">
                <Layers className="h-5 w-5 text-primary" /> TRADE HISTORY
              </CardTitle>
              <CardDescription className="text-slate-400">Past executions on Polymarket network</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {!data?.recent_trades || data.recent_trades.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-32 text-slate-300">
                   <Activity className="h-12 w-12 mb-4 opacity-10" />
                   <p className="text-sm font-medium">No activity records found.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {data.recent_trades.map((t, i) => (
                    <div key={i} className="flex items-center justify-between p-8 hover:bg-slate-50/50 transition-colors group">
                       <div className="space-y-1.5 min-w-0">
                         <p className="text-sm font-bold text-slate-800 group-hover:text-primary transition-colors line-clamp-1">{t.market}</p>
                         <div className="flex gap-3 items-center">
                            <span className={cn("inline-flex items-center gap-1 text-[10px] font-bold uppercase", t.side === 'BUY' ? "text-emerald-500" : "text-rose-500")}>
                                {t.side === 'BUY' ? "BUY ORDER" : "SELL ORDER"}
                            </span>
                            <span className="h-1 w-1 rounded-full bg-slate-200" />
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t.outcome} @ ${t.price}</span>
                         </div>
                       </div>
                       <div className="text-right">
                         <p className="text-sm font-bold text-slate-900">{t.size} shares</p>
                         <div className="flex items-center justify-end gap-1.5 mt-0.5">
                            <div className="h-1 w-1 rounded-full bg-emerald-400" />
                            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Network Confirmed</p>
                         </div>
                       </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )
      case "RISK":
        return (
          <div className="grid gap-10 md:grid-cols-2">
            <Card className="border-slate-200 bg-white shadow-sm rounded-[2.5rem] p-4 flex flex-col items-center justify-center text-center">
              <CardHeader className="p-8 pb-4">
                <div className="h-14 w-14 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 mb-4 mx-auto">
                    <Shield className="h-7 w-7" />
                </div>
                <CardTitle className="text-xl font-bold">Risk Management</CardTitle>
                <CardDescription className="text-slate-400">Autonomous protection settings</CardDescription>
              </CardHeader>
              <CardContent className="p-8 pt-4 w-full space-y-8">
                <div className="space-y-3 text-left">
                    <label className="text-[10px] uppercase font-bold text-slate-400 tracking-[0.1em]">MAX TRADE SIZE (USDC)</label>
                    <Input 
                        type="number" 
                        defaultValue={data?.settings?.max_trade_amount || 2}
                        className="bg-slate-50 border-slate-100 rounded-2xl h-14 text-sm font-bold focus:ring-primary"
                        onBlur={(e) => saveSettings('max_trade_amount', e.target.value)}
                    />
                </div>
                <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3 text-left">
                        <label className="text-[10px] uppercase font-bold text-emerald-600/60 tracking-[0.1em]">TAKE PROFIT %</label>
                        <div className="relative">
                            <Input 
                                type="number" 
                                defaultValue={data?.settings?.take_profit || 30.0}
                                className="bg-slate-50 border-slate-100 rounded-2xl h-14 pl-5 font-bold"
                                onBlur={(e) => saveSettings('take_profit', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="space-y-3 text-left">
                        <label className="text-[10px] uppercase font-bold text-rose-600/60 tracking-[0.1em]">STOP LOSS %</label>
                        <div className="relative">
                            <Input 
                                type="number" 
                                defaultValue={data?.settings?.stop_loss || -50.0}
                                className="bg-slate-50 border-slate-100 rounded-2xl h-14 pl-5 font-bold"
                                onBlur={(e) => saveSettings('stop_loss', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <Button 
                    className="w-full rounded-2xl h-14 font-bold uppercase text-[11px] tracking-wider shadow-md bg-primary text-white hover:bg-primary/95 transition-all"
                    disabled={saving}
                >
                    {saving ? "Saving Changes..." : "Apply Security Rules"}
                </Button>
              </CardContent>
            </Card>
            <div className="space-y-6">
                <Card className="border-slate-200 bg-white shadow-sm rounded-[2.5rem] p-10 group overflow-hidden relative">
                   <div className="absolute top-0 right-0 p-8 transform translate-x-1/2 -translate-y-1/2 opacity-[0.03] group-hover:scale-110 transition-transform duration-700">
                      <Lock className="h-32 w-32" />
                   </div>
                   <div className="flex items-center gap-4 mb-6">
                      <div className="h-10 w-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600">
                          <Lock className="h-5 w-5" />
                      </div>
                      <h3 className="font-bold text-slate-800 uppercase text-xs tracking-widest">Vault Protection</h3>
                   </div>
                   <p className="text-sm leading-relaxed text-slate-500 font-medium mb-6">Private keys are hardware-encrypted and never leave the bot&apos;s execution core. API sessions are ephemeral and monitored in real-time.</p>
                   <div className="h-px w-full bg-slate-50 mb-6" />
                   <div className="flex justify-between items-center bg-slate-50 p-4 rounded-xl">
                      <span className="text-[10px] font-bold text-slate-400">ENCRYPTION STATE</span>
                      <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3" /> FIPS-COMPLIANT
                      </span>
                   </div>
                </Card>
                <div className="p-8 rounded-[2rem] bg-indigo-600 text-white shadow-lg shadow-indigo-200 relative overflow-hidden group">
                   <Zap className="absolute right-[-10px] bottom-[-10px] h-24 w-24 opacity-10 rotate-12 group-hover:rotate-0 transition-transform duration-500" />
                   <h4 className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">Network Reliability</h4>
                   <p className="text-lg font-bold mb-4 tracking-tight tabular-nums">VAR_POLY_NODE_99.9%</p>
                   <div className="h-1.5 w-full bg-indigo-500/50 rounded-full overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: "99.9%" }} className="h-full bg-white/60" />
                   </div>
                </div>
            </div>
          </div>
        )
      default: // OVERVIEW
        return (
            <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <StatCard title="Wallet Balance" value={data ? `$${data.balance.toFixed(2)}` : "..."} icon={Wallet} subtext="USDC Mainnet" trend="up" />
              <StatCard title="Limit Orders" value={data ? data.open_orders.length.toString() : "0"} icon={RefreshCw} subtext="Open Trades" />
              <StatCard title="Market Share" value={data ? data.positions.length.toString() : "0"} icon={PieChart} subtext="Risk Positions" />
              <StatCard title="AI Intelligence" value={data?.last_scan || "--:--"} icon={Monitor} subtext="System Scan" />
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-12">
              <div className="lg:col-span-8 space-y-8">
                <Card className="border-slate-200 bg-white shadow-sm p-10 rounded-[3rem]">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                          <h4 className="font-bold text-slate-800 uppercase text-xs tracking-widest">Growth Performance</h4>
                          <p className="text-[11px] font-medium text-slate-400 mt-0.5">Consolidated portfolio valuation over time</p>
                        </div>
                        <div className="flex gap-2">
                           <Button size="sm" variant="outline" className="h-8 rounded-lg text-[10px] font-bold">24H</Button>
                           <Button size="sm" variant="ghost" className="h-8 rounded-lg text-[10px] font-bold text-slate-400">7D</Button>
                        </div>
                    </div>
                    <PerformanceChart data={data?.balance_history || []} />
                </Card>

                <Card className="border-slate-200 bg-white shadow-sm rounded-[3rem] overflow-hidden">
                    <CardHeader className="p-8 border-b border-slate-50 flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-800">Portfolio Breakdown</CardTitle>
                            <CardDescription className="text-[11px] font-medium text-slate-400">Active contracts across prediction domains</CardDescription>
                        </div>
                        <div className="h-2 w-2 rounded-full bg-primary" />
                    </CardHeader>
                    <CardContent className="p-8 pt-6">
                    {!data ? (
                        <div className="space-y-4 py-10 opacity-10"><div className="h-16 w-full bg-slate-200 animate-pulse rounded-[1.5rem]" /></div>
                    ) : (
                        <div className="space-y-4">
                            {data.positions.length === 0 ? (
                            <div className="py-24 border-2 border-dashed border-slate-100 rounded-[2rem] flex flex-col items-center justify-center text-slate-300">
                                <LayoutDashboard className="h-10 w-10 mb-4 opacity-10" />
                                <p className="text-sm font-medium italic">No active contracts found.</p>
                            </div>
                            ) : (
                            <div className="grid gap-4">
                                {data.positions.map((p, i) => (
                                <div key={i} className="flex items-center justify-between p-6 rounded-2xl bg-slate-50 border border-slate-100/50 hover:bg-white hover:border-slate-200 hover:shadow-md transition-all duration-300 group">
                                    <div className="space-y-1.5 min-w-0">
                                        <p className="text-sm font-bold text-slate-800 leading-tight line-clamp-1">{p.market}</p>
                                        <div className="flex items-center gap-3">
                                            <span className={cn("px-2 py-0.5 rounded-lg text-[9px] font-bold tracking-widest", 
                                                p.outcome === 'Yes' || p.outcome === 'Up' ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600")}>
                                                {p.outcome.toUpperCase()}
                                            </span>
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{p.size} shares</span>
                                        </div>
                                    </div>
                                    <div className="text-right flex flex-col items-end gap-1.5">
                                        <p className="text-base font-bold text-slate-900 tabular-nums leading-none">${(p.curPrice || 0).toFixed(3)}</p>
                                        {p.percentPnl !== undefined && (
                                            <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full", 
                                                p.percentPnl >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700")}>
                                                {p.percentPnl >= 0 ? "+" : ""}{p.percentPnl.toFixed(1)}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                                ))}
                            </div>
                            )}
                        </div>
                    )}
                    </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-4 space-y-8">
                <Card className="border-slate-200 bg-[#F9FAFB] shadow-sm rounded-[2.5rem] overflow-hidden flex flex-col">
                    <CardHeader className="py-6 px-8 bg-white border-b border-slate-100 flex flex-row items-center justify-between font-bold">
                        <div className="flex items-center gap-3 text-slate-800 uppercase text-[10px] tracking-widest">
                            <Terminal className="h-4 w-4 text-primary" /> Kernel Output
                        </div>
                        <div className="flex gap-1.5 grayscale opacity-30">
                           <div className="h-1 w-1 rounded-full bg-slate-800" />
                           <div className="h-1 w-1 rounded-full bg-slate-800" />
                        </div>
                    </CardHeader>
                    <CardContent className="p-0 flex-1">
                    <div className="h-[600px] w-full p-8 font-mono text-[11px] leading-relaxed text-slate-500 overflow-y-auto selection:bg-primary/20 scrollbar-thin scrollbar-thumb-slate-200 terminal-mask">
                        {data?.logs.map((log, i) => {
                            const isTrade = log.includes("SIGNAL") || log.includes("TRADE") || log.includes("BUY") || log.includes("SELL");
                            const isAI = log.includes("LOCAL AI");
                            return (
                            <div key={i} className={cn("mb-4 flex gap-4 p-4 rounded-xl transition-all duration-300", 
                                isTrade ? "bg-white text-slate-900 border border-slate-100 shadow-sm" : 
                                isAI ? "bg-indigo-50/40 text-indigo-700 border border-indigo-100/50" : 
                                "bg-transparent opacity-60 hover:opacity-100"
                            )}>
                                <span className="opacity-30 shrink-0 tabular-nums">{log.slice(1, 9)}</span>
                                <span className={cn("break-words", log.includes("ERROR") && "text-rose-600 font-bold")}>
                                    {log.split(": ").slice(1).join(": ")}
                                </span>
                            </div>
                            )
                        }) || <div className="p-20 text-center text-slate-300 font-bold uppercase tracking-[0.2em] italic">Initializing...</div>}
                    </div>
                    </CardContent>
                </Card>

                <Card className="border-slate-200 bg-white shadow-sm rounded-[2rem] p-8 space-y-6 relative group overflow-hidden">
                    <h5 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
                        <Monitor className="h-3 w-3" /> Diagnostics
                    </h5>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center text-xs">
                            <span className="font-medium text-slate-400">Core Neural</span>
                            <span className="font-bold text-slate-800">Llama 3.2</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="font-medium text-slate-400">Sync Status</span>
                            <span className="font-bold text-emerald-600">Active Sec</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="font-medium text-slate-400">Net_Latency</span>
                            <span className="font-bold text-slate-800">22ms</span>
                        </div>
                    </div>
                </Card>

                <Button variant="outline" className="w-full h-16 rounded-[1.5rem] border-slate-200 hover:bg-slate-50 text-slate-400 hover:text-slate-800 transition-all flex items-center justify-center gap-3">
                    <ExternalLink className="h-4 w-4" />
                    <span className="text-[11px] font-bold uppercase tracking-widest">Polyscan View</span>
                </Button>
              </div>
            </div>
            </>
        )
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 p-0 m-0 font-sans selection:bg-primary/20">
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-slate-900/10 md:hidden backdrop-blur-md"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 z-[110] w-[280px] bg-white border-r border-slate-100 p-8 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-16 px-2">
                 <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center text-white text-xl font-bold">P</div>
                 <h2 className="text-xl font-bold tracking-tight">PolyClaw</h2>
              </div>
              <nav className="flex-1 space-y-2">
                {['OVERVIEW', 'ACTIVITY', 'RISK'].map(item => (
                  <button 
                    key={item} 
                    onClick={() => { setActiveTab(item); setSidebarOpen(false); }}
                    className={cn(
                        "flex items-center w-full py-4 px-5 rounded-2xl text-[11px] font-bold tracking-widest transition-all", 
                        activeTab === item ? "bg-slate-100 text-primary shadow-sm" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                    )}
                  >
                    {item === 'OVERVIEW' && <LayoutDashboard className="mr-4 h-4 w-4" />}
                    {item === 'ACTIVITY' && <Layers className="mr-4 h-4 w-4" />}
                    {item === 'RISK' && <Shield className="mr-4 h-4 w-4" />}
                    {item}
                  </button>
                ))}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="flex min-h-screen">
         <aside className="hidden md:flex flex-col w-[320px] p-10 sticky top-0 h-screen overflow-hidden border-r border-slate-200 bg-white">
            <div className="flex items-center gap-4 mb-20 relative px-2">
               <div className="h-11 w-11 rounded-2xl bg-primary flex items-center justify-center text-white text-xl font-extrabold shadow-lg shadow-primary/30">C</div>
               <div className="space-y-0.5">
                  <h2 className="text-xl font-bold tracking-tight text-slate-900">PolyClaw</h2>
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400">Alpha Core v2</p>
               </div>
            </div>
            <nav className="flex-1 space-y-1">
              <button 
                className={cn("w-full flex items-center h-14 px-6 rounded-2xl text-[11px] font-bold uppercase tracking-widest transition-all group", 
                  activeTab === "OVERVIEW" ? "bg-slate-50 text-primary" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50/50")}
                onClick={() => setActiveTab("OVERVIEW")}
              >
                <LayoutDashboard className={cn("mr-4 h-4 w-4 transition-transform group-hover:scale-110", activeTab === "OVERVIEW" ? "text-primary" : "")} /> OVERVIEW
              </button>
              <button 
                className={cn("w-full flex items-center h-14 px-6 rounded-2xl text-[11px] font-bold uppercase tracking-widest transition-all group", 
                  activeTab === "ACTIVITY" ? "bg-slate-50 text-primary" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50/50")}
                onClick={() => setActiveTab("ACTIVITY")}
              >
                <Layers className={cn("mr-4 h-4 w-4 transition-transform group-hover:scale-110", activeTab === "ACTIVITY" ? "text-primary" : "")} /> ACTIVITY
              </button>
              <button 
                className={cn("w-full flex items-center h-14 px-6 rounded-2xl text-[11px] font-bold uppercase tracking-widest transition-all group", 
                  activeTab === "RISK" ? "bg-slate-50 text-primary" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50/50")}
                onClick={() => setActiveTab("RISK")}
              >
                <Shield className={cn("mr-4 h-4 w-4 transition-transform group-hover:scale-110", activeTab === "RISK" ? "text-primary" : "")} /> RISK_PROFILE
              </button>
            </nav>

            <div className="mt-auto space-y-5">
               <div className="p-6 rounded-[2rem] bg-indigo-50 border border-indigo-100 relative group overflow-hidden">
                  <div className="absolute right-[-10px] bottom-[-10px] opacity-[0.05] group-hover:scale-110 transition-transform">
                     <Zap className="h-16 w-16" />
                  </div>
                  <p className="text-[10px] font-bold uppercase text-indigo-400 tracking-widest mb-3 flex items-center gap-2">
                    <Globe className="h-3 w-3" /> Node
                  </p>
                  <p className="text-xs font-bold text-indigo-900">POLYGON_MAINNET</p>
               </div>
               <div className="flex items-center gap-3 px-6 mt-4 opacity-50">
                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_#10b981]" />
                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest leading-none">Healthy Sync</p>
               </div>
            </div>
         </aside>

         <main className="flex-1 px-8 md:px-16 py-16 w-full space-y-16 relative">
            <header className="flex flex-col md:flex-row md:items-end justify-between gap-10">
              <div className="space-y-4">
                <Button variant="outline" size="icon" className="md:hidden h-12 w-12 rounded-xl border-slate-200 bg-white mb-4" onClick={() => setSidebarOpen(true)}>
                  <Menu className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-6xl font-bold tracking-tight text-slate-900 border-l-4 border-primary pl-6 leading-none">{activeTab}</h1>
                    <div className="flex items-center gap-4 mt-6 pl-6">
                        <StatusBadge status={data?.status || "Kernel Standby"} />
                        <span className="text-slate-300 font-medium">/</span>
                        <span className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Scanner Sync: {data?.last_scan || "--:--"}</span>
                    </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="h-14 px-8 rounded-2xl border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-bold uppercase tracking-widest shadow-sm">Report</Button>
                <Button className="h-14 px-8 rounded-2xl bg-primary text-white text-[11px] font-bold uppercase tracking-widest shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all">Restart Core</Button>
              </div>
            </header>

            <motion.div 
              key={activeTab}
              initial={{ opacity: 0, y: 10 }} 
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="pb-20"
            >
               {renderContent()}
            </motion.div>
         </main>
      </div>
      <ChatWidget />
      
      <style jsx global>{`
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 20px; }
        ::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
      `}</style>
    </div>
  )
}
